// A screen title that admins can edit in place: tap "Rename", type, Save. Used by room info and workspace.
import React, { useState } from 'react';
import { View, Pressable, StyleSheet, Alert, TextStyle } from 'react-native';
import { Text, TextInput } from './Text';
import { Btn } from './Btn';
import { radius, makeStyles, useTheme } from '../theme';

export function RenameTitle({ name, canRename, onSave, textStyle, placeholder = 'Name' }: {
  name: string; canRename: boolean; onSave: (name: string) => Promise<void>; textStyle?: TextStyle; placeholder?: string;
}) {
  const theme = useTheme(); const s = useStyles();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [busy, setBusy] = useState(false);
  const start = () => { setDraft(name); setEditing(true); };
  const save = async () => {
    const n = draft.trim(); if (!n || n === name) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(n); setEditing(false); }
    catch (e: any) { Alert.alert('Could not rename', e?.message || 'Unknown error'); }
    finally { setBusy(false); }
  };
  if (editing) {
    return (
      <View>
        <TextInput
          style={s.input} value={draft} onChangeText={setDraft} autoFocus maxLength={80}
          placeholder={placeholder} placeholderTextColor={theme.faint} returnKeyType="done" onSubmitEditing={save} submitBehavior="blurAndSubmit"
        />
        <View style={s.row}>
          <Btn label="Save" small onPress={save} busy={busy} disabled={!draft.trim()} />
          <Btn label="Cancel" small kind="ghost" onPress={() => setEditing(false)} disabled={busy} />
        </View>
      </View>
    );
  }
  return (
    <View style={s.titleRow}>
      <Text style={[s.title, textStyle]} numberOfLines={2}>{name}</Text>
      {canRename && (
        <Pressable onPress={start} hitSlop={8} accessibilityRole="button" accessibilityLabel="Rename">
          <Text style={s.link}>Rename</Text>
        </Pressable>
      )}
    </View>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  titleRow: { flexDirection: 'row', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' },
  title: { color: theme.ink, fontSize: 22, fontWeight: '800', flexShrink: 1 },
  link: { color: theme.accent, fontSize: 13, fontWeight: '700' },
  input: { backgroundColor: theme.surface2, borderWidth: 1, borderColor: theme.accentBorder, borderRadius: radius.md, color: theme.ink, fontSize: 18, fontWeight: '700', paddingHorizontal: 12, paddingVertical: 8 },
  row: { flexDirection: 'row', gap: 8, marginTop: 8 },
}));
